import { ReactNode, useState } from 'react';
import { useMutation } from 'react-query';
import { api } from '../../../services/api';
import { Panel, PanelHeader, PanelBody } from '../../ui/Panel';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import MoreMenu from '../../ui/MoreMenu';
import { confirmAction } from '../../ui/Confirm';
import { ErrorNotice, SkeletonRows } from '../../ui/States';
import { errorMessage, inputClass } from '../../../lib/programs';
import { CommsData, CommsItem, CopyStatus, shortDay, useEventComms, useInfoRefresh } from '../../../lib/eventInfo';
import type { SimplePanelProps } from '../adapt';
import { th, td, headRow, em } from '../logistics';

const VALUE_FIELDS: [string, string, 'date' | 'url'][] = [
  ['form_by', 'Answer the form by', 'date'],
  ['book_travel_by', 'Book travel by', 'date'],
  ['eventbrite_link', 'Eventbrite link', 'url'],
];

const toInput = (c: CommsItem) => ({
  id: c.id,
  audienceKey: c.audienceKey,
  type: c.type,
  label: c.customLabel,
  offsetDays: c.offsetDays,
  sendOn: c.fixedSendOn,
  copyStatus: c.copyStatus,
  copyLink: c.copyLink,
  values: c.values,
});

/** Stacks on a phone: one block per row, each cell prefixed with its column name. */
const cell = `${td} max-md:block max-md:py-0 max-md:pr-0 max-md:empty:hidden max-md:before:content-[attr(data-label)_'_·_'] max-md:before:text-small max-md:before:text-ink-3`;

function Field({
  title,
  children,
  wide = false,
  hint,
}: {
  title: string;
  children: ReactNode;
  wide?: boolean;
  hint?: ReactNode;
}) {
  return (
    <div className={wide ? 'col-span-2 max-md:col-span-1' : ''}>
      <label className="block text-[15px] leading-5 font-semibold text-ink mb-[6px]">{title}</label>
      {children}
      {hint ? <p className="text-small text-ink-3 mt-1">{hint}</p> : null}
    </div>
  );
}
const grid = 'grid grid-cols-2 gap-x-5 gap-y-4 max-md:grid-cols-1';

function AddToPlan({
  d,
  saving,
  onSave,
  onClose,
}: {
  d: CommsData;
  saving: boolean;
  onSave: (item: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const [adding, setAdding] = useState({ audienceKey: '', type: 'custom', label: '' });
  return (
    <Modal
      title="Add an email to your plan"
      why="A message you write and send yourself. Its send date follows the message type; you can change it after."
      onClose={onClose}
      footer={
        <>
          <Button
            variant="primary"
            disabled={!adding.audienceKey || saving}
            onClick={() => onSave({ audienceKey: adding.audienceKey, type: adding.type, label: adding.label || null })}
          >
            Add to the plan
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          {!adding.audienceKey ? <span className="text-small text-ink-3">Choose an audience first.</span> : null}
        </>
      }
    >
      <div className={grid}>
        <Field title="Audience">
          <select
            className={inputClass}
            value={adding.audienceKey}
            onChange={(e) => setAdding({ ...adding, audienceKey: e.target.value })}
          >
            <option value="">Choose…</option>
            {d.audiences.map((a) => (
              <option key={a.key} value={a.key}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Message">
          <select
            className={inputClass}
            value={adding.type}
            onChange={(e) => setAdding({ ...adding, type: e.target.value })}
          >
            {Object.entries(d.types).map(([k, t]) => (
              <option key={k} value={k}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Label (optional)" wide hint="The message type's name is used when it's empty.">
          <input
            className={inputClass}
            value={adding.label}
            onChange={(e) => setAdding({ ...adding, label: e.target.value })}
          />
        </Field>
      </div>
    </Modal>
  );
}

function EditPlanItem({
  item,
  d,
  saving,
  onSave,
  onRemove,
  onClose,
}: {
  item: CommsItem;
  d: CommsData;
  saving: boolean;
  onSave: (draft: CommsItem) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<CommsItem>({ ...item });
  return (
    <Modal
      title={item.label}
      why={`For ${item.audience}. The copy is written outside the platform; the plan keeps its status and the facts it needs.`}
      onClose={onClose}
      width={640}
      footer={
        <>
          <Button variant="primary" disabled={saving} onClick={() => onSave(draft)}>
            Save
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="destructive" className="ml-auto" disabled={saving} onClick={onRemove}>
            Remove from the plan
          </Button>
        </>
      }
    >
      <div className={grid}>
        <Field title="Label" wide>
          <input
            className={inputClass}
            placeholder={draft.typeLabel}
            value={draft.customLabel ?? ''}
            onChange={(e) => setDraft({ ...draft, customLabel: e.target.value || null })}
          />
        </Field>
        <Field title="Send on" hint={`Empty: ${draft.offsetDays} days from the first day.`}>
          <input
            type="date"
            className={inputClass}
            value={draft.fixedSendOn ?? ''}
            onChange={(e) => setDraft({ ...draft, fixedSendOn: e.target.value || null })}
          />
        </Field>
        <Field title="Copy">
          <select
            className={inputClass}
            value={draft.copyStatus}
            onChange={(e) => setDraft({ ...draft, copyStatus: e.target.value as CopyStatus })}
          >
            {Object.entries(d.copyStatuses).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Where the copy is (link)" wide>
          <input
            className={inputClass}
            value={draft.copyLink ?? ''}
            onChange={(e) => setDraft({ ...draft, copyLink: e.target.value || null })}
          />
        </Field>
        {VALUE_FIELDS.filter(([k]) => d.types[item.type]?.slots.includes(k)).map(([k, label, kind]) => (
          <Field key={k} title={label}>
            <input
              type={kind === 'date' ? 'date' : 'text'}
              className={inputClass}
              value={draft.values[k] ?? ''}
              onChange={(e) => setDraft({ ...draft, values: { ...draft.values, [k]: e.target.value } })}
            />
          </Field>
        ))}
      </div>
    </Modal>
  );
}

/**
 * The communications plan (Before → Emails): which message goes to which audience when, and
 * which facts each still needs. The copy itself is written outside; the plan holds its status.
 */
export default function CommsPlanPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useEventComms(eventId);
  const refresh = useInfoRefresh(eventId);
  const [editing, setEditing] = useState<CommsItem | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savePlan = useMutation(
    async (items: Record<string, unknown>[]) => api.put(`/events/${eventId}/info/comms`, { items }),
    {
      onSuccess: () => {
        setEditing(null);
        setAdding(false);
        refresh();
      },
      onError: (err) => setError(errorMessage(err, 'Could not save the plan.')),
    },
  );
  const sent = useMutation(
    async ({ id, undo }: { id: string; undo: boolean }) =>
      api.post(`/events/${eventId}/info/comms/${encodeURIComponent(id)}/sent`, { undo }),
    {
      onSuccess: refresh,
      onError: (err) => setError(errorMessage(err, 'Could not record it.')),
    },
  );

  if (q.isLoading) return <SkeletonRows rows={5} />;
  if (q.error || !q.data) return <ErrorNotice>{errorMessage(q.error, 'Could not load the plan.')}</ErrorNotice>;
  const d = q.data;
  const items = d.items;
  const save = (list: Record<string, unknown>[]) => {
    setError(null);
    savePlan.mutate(list);
  };
  const remove = async (c: CommsItem) => {
    if (!(await confirmAction(`Remove “${c.label}” for ${c.audience} from the plan?`, 'Remove from the plan'))) return;
    save(items.filter((x) => x.id !== c.id).map(toInput));
  };
  const note = `${d.counts.sent} of ${d.counts.planned} sent${
    d.counts.overdue ? ` · ${d.counts.overdue} past ${d.counts.overdue === 1 ? 'its' : 'their'} date` : ''
  }`;

  return (
    <Panel>
      <PanelHeader title="Emails you send yourself" note={note}>
        {canEdit ? <Button onClick={() => setAdding(true)}>Add to the plan</Button> : null}
      </PanelHeader>
      <PanelBody>
        <p className="text-[15px] leading-[22px] text-ink-2 mb-3 max-w-[72ch]">
          Your plan, per audience. The platform never sends these: it shows when each one is due and which facts it
          still needs. Write and send each from your own email, then press &ldquo;I sent it&rdquo;.
        </p>
        {!items.length ? (
          <p className="text-[15px] text-ink py-4">Nothing is in your plan yet.</p>
        ) : (
          <table className="w-full tabular max-md:block">
            <thead className="max-md:hidden">
              <tr className={headRow}>
                <th className={`${th} w-[130px]`}>Send on</th>
                <th className={th}>Audience</th>
                <th className={th}>Message</th>
                <th className={th}>Copy</th>
                <th className={th}>Still needs</th>
                {canEdit ? (
                  <th className={th}>
                    <span className="sr-only">Actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody className="max-md:block">
              {items.map((c) => {
                const late = c.state === 'overdue';
                return (
                  <tr key={c.id} className="border-b border-line hover:bg-selected max-md:block max-md:py-3">
                    <td className={`${td} whitespace-nowrap max-md:block max-md:p-0`}>
                      <span className={late ? 'font-semibold' : ''}>{c.sendOn ? shortDay(c.sendOn) : '–'}</span>
                      {c.sentAt ? (
                        <span className={em}>Sent {shortDay(c.sentAt.slice(0, 10))}</span>
                      ) : late ? (
                        <span className={em}>Past its date: not marked sent yet</span>
                      ) : c.state === 'soon' ? (
                        <span className={em}>This week</span>
                      ) : null}
                    </td>
                    <td className={cell} data-label="To">
                      {c.audience}
                    </td>
                    <td className={cell} data-label="Message">
                      {c.label}
                      {c.copyLink ? (
                        <>
                          {' · '}
                          <a href={c.copyLink} target="_blank" rel="noreferrer">
                            copy
                          </a>
                        </>
                      ) : null}
                    </td>
                    <td className={`${cell} text-ink-2 md:whitespace-nowrap`} data-label="Copy">
                      {d.copyStatuses[c.copyStatus]}
                    </td>
                    <td className={`${cell} text-ink-2`} data-label="Needs">
                      {c.sentAt ? <span className="text-ink-3">–</span> : c.missing.join(', ') || 'Ready'}
                    </td>
                    {canEdit ? (
                      <td
                        className={`${td} whitespace-nowrap text-right max-md:block max-md:p-0 max-md:pt-2 max-md:text-left`}
                      >
                        {c.sentAt ? (
                          <Button
                            size="sm"
                            variant="quiet"
                            disabled={sent.isLoading}
                            onClick={() => sent.mutate({ id: c.id, undo: true })}
                          >
                            Undo sent
                          </Button>
                        ) : (
                          <span className="inline-flex gap-2 items-center">
                            <Button
                              size="sm"
                              disabled={sent.isLoading}
                              onClick={() => sent.mutate({ id: c.id, undo: false })}
                            >
                              I sent it
                            </Button>
                            <MoreMenu
                              label={`More for ${c.label}, ${c.audience}`}
                              items={[
                                { label: 'Edit', onClick: () => setEditing(c) },
                                { label: 'Remove from the plan', onClick: () => remove(c), destructive: true },
                              ]}
                            />
                          </span>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {items.some((c) => c.state === 'overdue') ? (
          <p className="text-small text-ink-3 mt-3">
            A date in bold has passed and the message isn&rsquo;t marked sent.
          </p>
        ) : null}
        {error ? (
          <div className="mt-3">
            <ErrorNotice>{error}</ErrorNotice>
          </div>
        ) : null}
      </PanelBody>
      {adding ? (
        <AddToPlan
          d={d}
          saving={savePlan.isLoading}
          onClose={() => setAdding(false)}
          onSave={(item) => save([...items.map(toInput), item])}
        />
      ) : null}
      {editing ? (
        <EditPlanItem
          item={editing}
          d={d}
          saving={savePlan.isLoading}
          onClose={() => setEditing(null)}
          onSave={(draft) => save(items.map((x) => toInput(x.id === draft.id ? draft : x)))}
          onRemove={() => remove(editing)}
        />
      ) : null}
    </Panel>
  );
}
